import {
  DEEP_LINK_SCHEME,
  isSupportedDeepLinkHost,
  resolvePathKind,
  type DeepLinkAction,
} from './deepLinkRoutes';

export type { DeepLinkAction };

const readQueryValue = (
  params: URLSearchParams,
  key: string,
): string | undefined => {
  const value = params.get(key);
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizePathname = (pathname: string): string => {
  if (!pathname) return '/';
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.slice(0, -1);
  }
  return withLeadingSlash;
};

/**
 * Hermes/RN `URL` treats many custom schemes as opaque:
 *   bizplus://inventory/new → hostname "", pathname "/", href "…/new/"
 * So we pull path + query from the raw href after `scheme://`.
 */
export const pathAndQueryFromCustomSchemeHref = (
  href: string,
  scheme: string,
): { pathname: string; searchParams: URLSearchParams } => {
  const prefix = `${scheme}://`;
  if (!href.toLowerCase().startsWith(prefix)) {
    return { pathname: '/', searchParams: new URLSearchParams() };
  }

  let rest = href.slice(prefix.length);
  const hashIdx = rest.indexOf('#');
  if (hashIdx >= 0) {
    rest = rest.slice(0, hashIdx);
  }

  let query = '';
  const qIdx = rest.indexOf('?');
  if (qIdx >= 0) {
    query = rest.slice(qIdx + 1);
    rest = rest.slice(0, qIdx);
  }

  return {
    pathname: normalizePathname(rest),
    searchParams: new URLSearchParams(query),
  };
};

export const parseDeepLink = (href: string): DeepLinkAction | null => {
  const trimmed = String(href || '').trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  const protocol = parsed.protocol.replace(':', '').toLowerCase();
  const host = parsed.hostname.toLowerCase();

  let pathname: string;
  let searchParams: URLSearchParams;

  if (protocol === DEEP_LINK_SCHEME) {
    // Always use raw-href extraction — Node and Hermes disagree on hostname.
    const fromRaw = pathAndQueryFromCustomSchemeHref(trimmed, DEEP_LINK_SCHEME);
    pathname = fromRaw.pathname;
    searchParams = fromRaw.searchParams;
  } else if (protocol === 'https' || protocol === 'http') {
    if (!isSupportedDeepLinkHost(host)) return null;
    pathname = normalizePathname(parsed.pathname);
    searchParams = parsed.searchParams;
  } else {
    return null;
  }

  const resolved = resolvePathKind(pathname);
  if (!resolved) return null;

  return {
    kind: resolved.kind,
    href: trimmed,
    src: readQueryValue(searchParams, 'src'),
    campaign: readQueryValue(searchParams, 'c'),
    param: resolved.param,
    notificationId:
      resolved.kind === 'notifications'
        ? readQueryValue(searchParams, 'id')
        : undefined,
  };
};

/** Pushes whose notice is in the inbox: tapping one opens it there. */
const INBOX_PUSH_TYPES = new Set(['payment_received', 'waiter_order']);

/**
 * Pushes that carry no url but a known type. They open the Notifications
 * screen on the notice, which opens the order it is about.
 */
export const hrefForTypedPush = (
  payload: Record<string, unknown>,
): string | null => {
  if (typeof payload.type !== 'string' || !INBOX_PUSH_TYPES.has(payload.type)) {
    return null;
  }
  const id =
    typeof payload.notificationId === 'string'
      ? payload.notificationId.trim()
      : '';
  return id
    ? `${DEEP_LINK_SCHEME}://notifications?id=${encodeURIComponent(id)}`
    : `${DEEP_LINK_SCHEME}://notifications`;
};

export const extractDeepLinkHrefFromNotification = (
  data: unknown,
): string | null => {
  if (!data || typeof data !== 'object') return null;
  const payload = data as Record<string, unknown>;
  const typed = hrefForTypedPush(payload);
  if (typed) return typed;
  const href = payload.url ?? payload.deeplink ?? payload.link;
  return typeof href === 'string' && href.trim().length > 0
    ? href.trim()
    : null;
};
